<script setup>
/**
 * ImmersiveOnboarding
 * Full-screen "how to use" intro for the immersive catalog. Shows an animated
 * swipe up/down SVG, a short explanation, a "don't show again" toggle, and a
 * big "המשך" button at the bottom. Purely presentational — the host decides
 * when to show it (every entry until the user opts out) and persists the
 * opt-out flag on dismiss based on the toggle state it owns via v-model.
 */
import { ref, onMounted, nextTick } from 'vue'

defineProps({
  dontShowAgain: { type: Boolean, default: false },
})
defineEmits(['dismiss', 'update:dontShowAgain'])

// Mouse-wheel / keyboard hints only apply where a mouse exists — touch-only
// devices (phones/tablets) skip that bullet.
const hasFinePointer = window.matchMedia?.('(hover: hover) and (pointer: fine)')?.matches ?? false

// Move focus into the dialog on open so keyboard/screen-reader users land here.
const continueBtn = ref(null)
onMounted(() => {
  nextTick(() => continueBtn.value?.focus())
})
</script>

<template>
  <div class="onb" dir="rtl" role="dialog" aria-modal="true" aria-label="מדריך מצב אימרסיבי">
    <div class="onb-content">
      <h2 class="onb-title">מצב קטלוג אימרסיבי</h2>
      <p class="onb-subtitle">דפדוף מהיר בין הפריטים — פריט אחד בכל פעם</p>

      <!-- Animated swipe up/down illustration -->
      <div class="onb-illustration" aria-hidden="true">
        <svg class="onb-svg" viewBox="0 0 120 180" fill="none">
          <!-- phone frame -->
          <rect
            x="28"
            y="12"
            width="64"
            height="156"
            rx="14"
            stroke="currentColor"
            stroke-width="2.5"
            opacity="0.45"
          />
          <!-- up chevron -->
          <polyline
            class="onb-chev onb-chev-up"
            points="48 44 60 32 72 44"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <!-- down chevron -->
          <polyline
            class="onb-chev onb-chev-down"
            points="48 136 60 148 72 136"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <!-- swiping finger -->
          <circle class="onb-finger" cx="60" cy="90" r="13" fill="currentColor" />
        </svg>
      </div>

      <ul class="onb-list">
        <li>
          <span class="onb-bullet onb-bullet-up">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </span>
          החליקו מעלה — הפריט הבא
        </li>
        <li>
          <span class="onb-bullet onb-bullet-down">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
          החליקו מטה — הפריט הקודם
        </li>
        <li>
          <span class="onb-bullet onb-bullet-subjump">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="18 11 12 5 6 11" />
              <polyline points="18 18 12 12 6 18" />
            </svg>
          </span>
          חץ כפול — קפיצה לתת־הקטגוריה הבאה או הקודמת
        </li>
        <li>
          <span class="onb-bullet onb-bullet-subsheet">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polygon points="12 2 2 7 12 12 22 7 12 2" />
              <polyline points="2 12 12 17 22 12" />
              <polyline points="2 17 12 22 22 17" />
            </svg>
          </span>
          לחצן השכבות שבין החצים — בחירת תת־קטגוריה מרשימה
        </li>
        <li>
          <span class="onb-bullet onb-bullet-cart">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
            </svg>
          </span>
          הוסיפו לעגלה ישירות מהמסך
        </li>
        <li v-if="hasFinePointer">
          <span class="onb-bullet onb-bullet-wheel">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="6" y="3" width="12" height="18" rx="6" />
              <path d="M12 7v4" />
            </svg>
          </span>
          גלגלת העכבר או חיצי המקלדת — דפדוף בין הפריטים
        </li>
      </ul>
    </div>

    <label class="onb-dontshow">
      <input
        type="checkbox"
        class="onb-dontshow-input"
        :checked="dontShowAgain"
        @change="$emit('update:dontShowAgain', $event.target.checked)"
      />
      <span class="onb-dontshow-track" aria-hidden="true">
        <span class="onb-dontshow-thumb"></span>
      </span>
      אל תציגו שוב
    </label>

    <button ref="continueBtn" class="onb-continue" type="button" @click="$emit('dismiss')">
      המשך
    </button>
  </div>
</template>

<style scoped>
.onb {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  background:
    radial-gradient(120% 70% at 50% 0%, rgba(59, 130, 246, 0.22) 0%, rgba(10, 15, 26, 0) 55%),
    rgba(10, 15, 26, 0.96);
  color: #ffffff;
  padding: calc(24px + env(safe-area-inset-top, 0px)) 24px
    calc(20px + env(safe-area-inset-bottom, 0px));
  -webkit-tap-highlight-color: transparent;
}

.onb-content {
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 360px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  text-align: center;
}

.onb-title {
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0;
}

.onb-subtitle {
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}

.onb-illustration {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 0;
}

.onb-svg {
  width: 110px;
  height: 165px;
  color: #60a5fa;
}

.onb-finger {
  animation: onb-swipe 2.4s ease-in-out infinite;
}

.onb-chev {
  opacity: 0.35;
}

.onb-chev-up {
  animation: onb-pulse-up 2.4s ease-in-out infinite;
}

.onb-chev-down {
  animation: onb-pulse-down 2.4s ease-in-out infinite;
}

@keyframes onb-swipe {
  0%,
  100% {
    transform: translateY(34px);
  }
  50% {
    transform: translateY(-34px);
  }
}

@keyframes onb-pulse-up {
  0%,
  45% {
    opacity: 0.35;
  }
  55%,
  100% {
    opacity: 1;
  }
}

@keyframes onb-pulse-down {
  0%,
  45% {
    opacity: 1;
  }
  55%,
  100% {
    opacity: 0.35;
  }
}

.onb-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.onb-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  text-align: start;
}

.onb-bullet {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.onb-bullet svg {
  width: 20px;
  height: 20px;
}

.onb-bullet-up {
  color: #34d399;
}

.onb-bullet-down {
  color: #60a5fa;
}

.onb-bullet-subjump {
  color: #93c5fd;
}

.onb-bullet-subsheet {
  color: #3b82f6;
}

.onb-bullet-cart {
  color: #fbbf24;
}

.onb-bullet-wheel {
  color: #cbd5e1;
}

.onb-dontshow {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 18px;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.onb-dontshow-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.onb-dontshow-track {
  position: relative;
  width: 40px;
  height: 22px;
  flex-shrink: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition:
    background 0.18s ease,
    border-color 0.18s ease;
}

.onb-dontshow-thumb {
  position: absolute;
  top: 2px;
  inset-inline-start: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ffffff;
  transition: inset-inline-start 0.18s ease;
}

.onb-dontshow-input:checked + .onb-dontshow-track {
  background: var(--color-success, #10b981);
  border-color: transparent;
}

.onb-dontshow-input:checked + .onb-dontshow-track .onb-dontshow-thumb {
  inset-inline-start: 20px;
}

.onb-dontshow-input:focus-visible + .onb-dontshow-track {
  outline: 2px solid #60a5fa;
  outline-offset: 2px;
}

.onb-continue {
  flex-shrink: 0;
  width: 100%;
  max-width: 360px;
  margin-top: 12px;
  padding: 16px;
  border: none;
  border-radius: 16px;
  background: var(--gradient-primary, linear-gradient(135deg, #1e293b 0%, #334155 100%));
  color: #ffffff;
  font-family: var(--font-family);
  font-size: 1.1rem;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(13, 71, 161, 0.35);
  transition: transform 0.1s ease;
  -webkit-tap-highlight-color: transparent;
}

.onb-continue:active {
  transform: scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .onb-finger,
  .onb-chev-up,
  .onb-chev-down {
    animation: none;
  }

  .onb-finger {
    transform: translateY(0);
  }

  .onb-chev {
    opacity: 0.7;
  }

  .onb-continue,
  .onb-dontshow-track,
  .onb-dontshow-thumb {
    transition: none;
  }
}
</style>
